import { useState, useEffect, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, SUPABASE_ENABLED } from '../lib/supabase';
import { setLoggerUser } from '../lib/logger';

export interface AuthState {
  user: User | null;
  loading: boolean;
  plan: 'free' | 'pro';
  teamSeats: number | null;  // null = no tiene plan Teams activo
  refreshPlan: () => Promise<void>;
}

export function useAuth(): AuthState {
  const [user, setUser]           = useState<User | null>(null);
  const [loading, setLoading]     = useState(true);
  const [plan, setPlan]           = useState<'free' | 'pro'>('free');
  const [teamSeats, setTeamSeats] = useState<number | null>(null);

  const fetchPlan = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('plan, team_seats')
        .eq('id', userId)
        .single();
      if (error) {
        console.warn('[useAuth] fetchPlan:', error.message);
        return;
      }
      if (data?.plan) setPlan(data.plan as 'free' | 'pro');
      setTeamSeats(data?.team_seats ?? null);
    } catch (err) {
      console.error('[useAuth] fetchPlan unexpected error:', err);
    }
  }, []);

  // Public method to force a plan refresh (e.g. after checkout success redirect)
  const refreshPlan = useCallback(async () => {
    if (user) await fetchPlan(user.id);
  }, [user, fetchPlan]);

  useEffect(() => {
    if (!SUPABASE_ENABLED) {
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }: any) => {
      const u = session?.user ?? null;
      setUser(u);
      setLoggerUser(u?.id ?? null);
      if (u) fetchPlan(u.id);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event: any, session: any) => {
      const u = session?.user ?? null;
      setUser(u);
      setLoggerUser(u?.id ?? null);
      if (u) fetchPlan(u.id);
      else setPlan('free');
    });

    return () => subscription.unsubscribe();
  }, [fetchPlan]);

  return { user, loading, plan, teamSeats, refreshPlan };
}
